import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { z } from 'zod';
import { LOAD_STATUSES } from '../../../carrier/loads/data/LoadDTO';

const zodString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

const DELAYED_LOAD_STAGES = ['pickup', 'delivery'] as const;
const DRIVERS_STAGES = ['available', 'assigned', 'delivering'] as const;

export type DelayedLoadStage = (typeof DELAYED_LOAD_STAGES)[number];
export type DispatchQueueStage = DelayedLoadStage | 'unassigned';
export type DriverStage = (typeof DRIVERS_STAGES)[number];

export const homePageParamsSchema = z.object({
  q: zodString,
  delayed_load_stage: zodString,
  driver_status: zodString,
  ratings_stage: z
    .enum(['ratings_received', 'ratings_to_give'])
    .catch('ratings_received'),
  activity_logs_level: zodString,
  activity_logs_category: zodString,
});

export type HomePageParams = z.infer<typeof homePageParamsSchema>;

const vehicleSchema = z.object({
  make: zodString,
  model: zodString,
  year: z.number().nullable().catch(null).default(null),
});

export const delayedLoadsCountSchema = z.object({
  pickup: z.number().nullable().catch(0).default(0),
  delivery: z.number().nullable().catch(0).default(0),
  unassigned: z.number().nullable().catch(0).default(0),
});

export type DelayedLoadsCountDTO = z.infer<typeof delayedLoadsCountSchema>;

export const driversCountSchema = z.object({
  available: z.number().nullable().catch(0).default(0),
  assigned: z.number().nullable().catch(0).default(0),
  delivering: z.number().nullable().catch(0).default(0),
  total: z.number().nullable().catch(0).default(0),
});

export type DriversCountDTO = z.infer<typeof driversCountSchema>;

export const delayedLoadSchema = z.object({
  guid: zodString,
  number: zodString,
  pickup_city: zodString,
  pickup_state: zodString,
  delivery_city: zodString,
  delivery_state: zodString,
  delayed_days: z.number().nullable().catch(null).default(null),
  vehicles: z.array(vehicleSchema).prefault([]),
});

export type DelayedLoadDTO = z.infer<typeof delayedLoadSchema>;

export const unassignedLoadSchema = z.object({
  guid: zodString,
  number: zodString,
  pickup_city: zodString,
  pickup_state: zodString,
  delivery_city: zodString,
  delivery_state: zodString,
  vehicles: z.array(vehicleSchema).prefault([]),
});

export type UnassignedLoadDTO = z.infer<typeof unassignedLoadSchema>;

export const availableDriverSchema = z.object({
  guid: zodString,
  name: zodString,
  phone: zodString,
  email: zodString,
  trailer_capacity: z.number().nullable().catch(null).default(null),
  connection_status: z
    .enum(['pending', 'activated', 'declined', 'deactivated', 'cancelled'])
    .catch('pending'),
  connection_type: z.enum(['invite', 'join_request']).catch('invite'),
  user_guid: zodString,
});

export type AvailableDriverDTO = z.infer<typeof availableDriverSchema>;

const assignedLoadSchema = z.object({
  guid: zodString,
  number: zodString,
  status: z.enum(LOAD_STATUSES).catch(LOAD_STATUSES[0]),
  pickup_city: zodString,
  pickup_state: zodString,
  delivery_city: zodString,
  delivery_state: zodString,
  vehicles: z.array(vehicleSchema).prefault([]),
});

export type AssignedLoadDTO = z.infer<typeof assignedLoadSchema>;

export const assignedDriverSchema = availableDriverSchema.extend({
  loads: z.array(assignedLoadSchema).prefault([]),
});

export type AssignedDriverDTO = z.infer<typeof assignedDriverSchema>;

export const deliveringDriverSchema = availableDriverSchema.extend({
  loads: z.array(assignedLoadSchema).prefault([]),
});

export type DeliveringDriverDTO = z.infer<typeof deliveringDriverSchema>;

export const driverSchema = availableDriverSchema.extend({
  status: z.enum(['available', 'assigned', 'delivering']).catch('available'),
  loads: z.array(assignedLoadSchema).prefault([]),
});

export type DriverDTO = z.infer<typeof driverSchema>;

const companyActivityActorSchema = z
  .object({
    type: z
      .enum(['user', 'carrier_public_api', 'system', 'super_dispatch_support'])
      .catch('user'),
    guid: zodString,
    text: zodString,
  })
  .nullable()
  .default(null);

const companyActivityTargetSchema = z
  .object({
    type: z.enum(['load', 'user']).catch('load'),
    guid: zodString,
    text: zodString,
  })
  .nullable()
  .default(null);

export const companyActivitySchema = z.object({
  guid: zodString,
  occurred_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  actor: companyActivityActorSchema,
  target: companyActivityTargetSchema,
  instructions: zodString,
  text: zodString,
  kind: z
    .enum([
      'security',
      'create',
      'approve',
      'reject',
      'update',
      'disable',
      'delete',
    ])
    .catch('security'),
  level: z.enum(['info', 'warning']).catch('info'),
  verb: z
    .enum([
      'user.invited',
      'user.deactivated',
      'user.updated',
      'user.removed',
      'user.roles_updated',
      'user.reinvited',
      'user.join_request_accepted',
      'load.reassigned',
      'security.suspicious_login_detected',
      'company.updated',
      'company.ach_account_number_updated',
      'load_offer.notifications_updated',
    ])
    .catch('user.invited'),
  category: z
    .enum(['security', 'user_management', 'operations', 'company_profile'])
    .catch('security'),
  details: z.array(zodString).prefault([]),
});

export type CompanyActivityDTO = z.infer<typeof companyActivitySchema>;

export const companyActivityParamsSchema = z.object({
  page: z.number().nullish(),
  page_size: z.number().nullish(),
  level: z.string().nullish(),
  category: z.string().nullish(),
  occurred_at_after: z.string().nullish(),
  occurred_at_before: z.string().nullish(),
});

export type CompanyActivityParams = z.infer<typeof companyActivityParamsSchema>;
